import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Library, Loader2, Music2, SlidersHorizontal, Upload, X } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import WaveCanvas from "@/components/recstudio/WaveCanvas";
import InstrumentalTempo from "@/components/recstudio/InstrumentalTempo";
import { MixBits } from "@/components/recstudio/TrackRow";
import { INSTRUMENTAL, adoptChannel, otherChannels, placeBuffer } from "@/components/recstudio/sessionEngine";
import { listSongs } from "@/lib/creatorApi";
import { uploadRecording } from "@/lib/recStudioApi";
import { safeApiError } from "@/lib/safeErrorText";

const opt = "flex w-full items-center gap-3 rounded-2xl border border-white/10 bg-white/[0.03] p-3 text-left text-[13px] transition-colors duration-200 hover:bg-white/[0.08] disabled:opacity-40";

function SongPicker({ open, onClose, onPick }) {
  const [songs, setSongs] = useState(null);
  useEffect(() => {
    if (open && songs === null) listSongs({ limit: 200 }).then((s) => setSongs(s.filter((x) => x.audio_url))).catch(() => setSongs([]));
  }, [open, songs]);
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="rec-song-picker" aria-describedby={undefined} className="max-w-lg border-white/10 bg-[#0b0e1c] text-white">
        <DialogHeader><DialogTitle className="font-display">Pick a song from My Songs</DialogTitle></DialogHeader>
        <div className="max-h-[60vh] space-y-1.5 overflow-y-auto">
          {songs === null ? <p className="flex items-center gap-2 text-white/60"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</p> : null}
          {(songs || []).map((s) => (
            <button key={s.id} type="button" data-testid={`rec-song-${s.id}`} onClick={() => onPick(s)} className={opt}><Music2 className="h-4 w-4 text-rose-200" /><span className="truncate">{s.title}</span></button>
          ))}
          {songs?.length === 0 ? <p className="text-white/55">No songs with audio yet.</p> : null}
        </div>
      </DialogContent>
    </Dialog>
  );
}

/** The music you sing over: upload a file, pick from My Songs, or reuse a track already in the Advanced Studio. */
export default function InstrumentalCard({ session, buffer, duration }) {
  const { doc, engine, change } = session;
  const inst = doc.instrumental;
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [picking, setPicking] = useState(false);
  const set = (next, opts) => change((d) => ({ ...d, instrumental: next }), opts);
  const fromFile = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const buf = await engine.ctx.decodeAudioData(await file.arrayBuffer());
      const rec = await uploadRecording(file, file.name, `Instrumental · ${file.name}`);
      placeBuffer(engine, doc.id, INSTRUMENTAL, buf, `Instrumental · ${file.name}`, rec.file_url);
      set({ url: rec.file_url, name: file.name.replace(/\.[^.]+$/, ""), duration: Math.round(buf.duration * 100) / 100, volume: 80, muted: false, solo: false });
      toast.success("Instrumental ready — press Record to sing over it");
    } catch (e) { toast.error("Couldn't use that file", { description: e?.response ? safeApiError(e) : "Use an MP3, WAV, M4A or FLAC audio file." }); }
    finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };
  const fromSong = (s) => { setPicking(false); set({ url: s.audio_url, name: s.title, duration: s.duration || 0, volume: 80, muted: false, solo: false, song_id: s.id, project_id: s.project_id || null, source_song_id: s.creator_meta?.mixed_from || null, ...(s.tempo ? { tempo: s.tempo } : {}) }, { reload: true }); };
  const fromChannel = async ({ c, idx }) => {
    setBusy(true);
    try {
      let url = c.sourceUrl?.startsWith("https://") ? c.sourceUrl : null;
      if (!url) url = (await uploadRecording(engine.channelToWavBlob(idx), "instrumental.wav", `Instrumental · ${c.name || `CH ${idx + 1}`}`)).file_url;
      const name = c.name || `Channel ${idx + 1}`;
      adoptChannel(engine, doc.id, INSTRUMENTAL, idx, url);
      set({ url, name, duration: Math.round(c.buffer.duration * 100) / 100, volume: c.volume ?? 80, muted: false, solo: false });
    } catch (e) { toast.error("Couldn't use that track", { description: safeApiError(e) }); }
    finally { setBusy(false); }
  };
  const others = otherChannels(engine, doc.id);
  if (inst) {
    return (
      <div data-testid="rec-instrumental" className="rounded-2xl border border-sky-300/25 bg-sky-500/[0.06] p-3">
        <div className="flex items-center gap-2">
          <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-sky-500/25"><Music2 className="h-3.5 w-3.5 text-sky-100" /></span>
          <div className="min-w-0 flex-1">
            <p data-testid="rec-instrumental-name" className="truncate font-display text-[14px] font-semibold">{inst.name}</p>
            <p className="font-mono text-[9px] uppercase tracking-[0.16em] text-white/40">Instrumental</p>
          </div>
          <button type="button" data-testid="rec-instrumental-remove" onClick={() => change((d) => ({ ...d, instrumental: null }), { reload: true })} aria-label="Remove instrumental" className="text-white/35 hover:text-rose-300"><X className="h-4 w-4" /></button>
        </div>
        {buffer ? <div className="mt-2"><WaveCanvas buffer={buffer} duration={duration} height={30} color="#7dd3fc" grid={inst.tempo} /></div> : null}
        <InstrumentalTempo session={session} buffer={buffer} />
        <div className="mt-2"><MixBits id="instrumental" part={inst} onChange={(p) => set({ ...inst, ...p })} /></div>
      </div>
    );
  }
  return (
    <div data-testid="rec-instrumental-picker" className="space-y-2 rounded-2xl border border-dashed border-sky-300/30 p-3">
      <p className="font-display text-[14px] font-semibold">Music to sing over</p>
      <input ref={fileRef} type="file" accept="audio/*" className="hidden" data-testid="rec-instrumental-file" onChange={(e) => fromFile(e.target.files?.[0])} />
      <button type="button" data-testid="rec-instrumental-upload" disabled={busy} onClick={() => fileRef.current?.click()} className={opt}>
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4 text-sky-200" />} Upload an instrumental
      </button>
      <button type="button" data-testid="rec-instrumental-library" disabled={busy} onClick={() => setPicking(true)} className={opt}><Library className="h-4 w-4 text-sky-200" /> Pick from My Songs</button>
      {others.length ? others.slice(0, 6).map((o) => (
        <button key={o.idx} type="button" data-testid={`rec-instrumental-channel-${o.idx}`} disabled={busy} onClick={() => fromChannel(o)} className={opt}>
          <SlidersHorizontal className="h-4 w-4 text-sky-200" /><span className="truncate">Use “{o.c.name || `CH ${o.idx + 1}`}” from the Advanced Studio</span>
        </button>
      )) : null}
      <SongPicker open={picking} onClose={() => setPicking(false)} onPick={fromSong} />
    </div>
  );
}
